import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import { useAuth } from '../contexts/AuthContext';
import { useEnterpriseContext } from '../contexts/EnterpriseContext';
import { getApiUrl } from '../config/environment';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import CoachReviewsSection from '../components/CoachReviewsSection';
import { motion } from 'framer-motion';
import { formatTimeFR, toLocalDateStr } from '../utils/formatFR';
import { Star, Clock, Award, ChevronRight, Loader2, Briefcase, Heart, Pencil, Crown, Sparkles } from 'lucide-react';

// Premium plan badge — shown publicly only for Pro / Elite coaches (Starter stays invisible to preserve premium positioning)
const PUBLIC_PLAN_BADGE = {
  pro: { label: 'Coach Pro', Icon: Sparkles, className: 'bg-[#FEF9E7] text-[#92710A] border-[#F5E6A3]' },
  elite: { label: 'Coach Elite', Icon: Crown, className: 'bg-purple-50 text-purple-700 border-purple-200' },
};

const API = getApiUrl();

export default function CoachPublicProfile() {
  const { coachId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { isCollaborator, eligibleCoachIds, orders, refresh: refreshEnterpriseCtx } = useEnterpriseContext();

  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState({});
  const [monthAvailability, setMonthAvailability] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  // Thème de session (obligatoire)
  const [themes, setThemes] = useState([]);
  const [selectedThemeSlug, setSelectedThemeSlug] = useState(null);
  const [customTheme, setCustomTheme] = useState('');

  // 🏢 B2B coverage calculée depuis le contexte (source unique serveur)
  const coachIsInB2BScope = isCollaborator && eligibleCoachIds.has(coachId);
  const coverageOrder = coachIsInB2BScope
    ? orders.find(o => (o.eligible_coach_ids || []).includes(coachId))
    : null;
  const enterpriseCoverage = coachIsInB2BScope && coverageOrder ? {
    sessions_remaining: coverageOrder.sessions_remaining,
    company_name: coverageOrder.company_name,
    enterprise_order_id: coverageOrder.order_id,
  } : null;

  useEffect(() => {
    const fetchCoach = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${API}/marketplace/coaches/${coachId}`, { headers });
        if (res.status === 403) {
          toast.error("Ce coach n'est pas dans le périmètre de votre forfait entreprise.");
          navigate('/marketplace');
          return;
        }
        if (!res.ok) throw new Error('Coach non trouvé');
        setCoach(await res.json());
      } catch (err) { toast.error('Coach non trouvé'); navigate('/marketplace'); }
      finally { setLoading(false); }
    };
    fetchCoach();
  }, [coachId, navigate, token]);

  useEffect(() => {
    fetch(`${API}/coach/themes`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setThemes(Array.isArray(data) ? data : []))
      .catch(() => setThemes([]));
  }, []);

  const fetchAvailability = useCallback(async (dateStr) => {
    try {
      const res = await fetch(`${API}/marketplace/coaches/${coachId}/availability?date=${dateStr}&days=7`);
      const data = await res.json();
      setAvailability(data.availability || {});
    } catch (err) { console.error(err); }
  }, [coachId]);

  useEffect(() => {
    if (coach) {
      fetchAvailability(toLocalDateStr(new Date()));
      // Fetch full month availability for calendar greying
      const today = new Date();
      const monthStart = toLocalDateStr(new Date(today.getFullYear(), today.getMonth(), 1));
      fetch(`${API}/marketplace/coaches/${coachId}/availability?date=${monthStart}&days=60`)
        .then(r => r.json())
        .then(d => setMonthAvailability(d.availability || {}))
        .catch(() => {});
    }
  }, [coach, fetchAvailability, coachId]);

  const handleDateSelect = (date) => {
    if (!date) return;
    setSelectedDate(date); setSelectedSlot(null);
    const dateStr = toLocalDateStr(date);
    // Always refetch to ensure fresh data (no stale cached slots)
    fetchAvailability(dateStr);
  };

  const handleBook = async () => {
    if (!user) { toast.error('Connectez-vous pour réserver'); navigate('/login'); return; }
    if (!selectedDate || !selectedSlot) return;

    // Validation du thème
    const isCustom = selectedThemeSlug === 'custom';
    const customLabel = customTheme.trim();
    if (!selectedThemeSlug) {
      toast.error('Veuillez sélectionner un thème de coaching');
      return;
    }
    if (isCustom && customLabel.length < 3) {
      toast.error('Précisez votre thème personnalisé (3 caractères minimum)');
      return;
    }
    const themeLabel = isCustom
      ? customLabel
      : (themes.find(t => t.slug === selectedThemeSlug)?.label || selectedThemeSlug);

    setBooking(true);
    try {
      const res = await fetch(`${API}/marketplace/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-origin-url': window.location.origin },
        body: JSON.stringify({
          coach_id: coachId,
          date: toLocalDateStr(selectedDate),
          start_time: selectedSlot.start_time,
          theme_slug: selectedThemeSlug,
          theme_label: themeLabel,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Erreur'); }
      const data = await res.json();
      if (data.enterprise_covered) {
        toast.success(data.message || 'Session réservée sans frais (forfait entreprise)');
        refreshEnterpriseCtx();  // refresh sessions_remaining dans l'UI globale
        navigate('/my-bookings');
        return;
      }
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) { toast.error(err.message); }
    finally { setBooking(false); }
  };

  if (loading) {
    return (
      <div className="theme-light min-h-screen pt-20 px-4">
        <div className="max-w-5xl mx-auto">
          <Skeleton className="h-72 w-full rounded-2xl bg-[#F0EDE6] mb-6" />
          <Skeleton className="h-8 w-48 bg-[#F0EDE6] mb-3" />
        </div>
      </div>
    );
  }
  if (!coach) return null;

  const selectedDateStr = selectedDate ? toLocalDateStr(selectedDate) : null;
  const daySlots = selectedDateStr ? availability[selectedDateStr] || [] : [];
  const sessionPrice = (coach.hourly_rate || 0) * (coach.session_duration || 60) / 60;

  return (
    <div className="theme-light min-h-screen">
      {/* Sticky nav */}
      <div className="sticky top-0 z-30 bg-[#F8F7F4]/90 backdrop-blur-lg border-b border-[#E8E5DE]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 sm:pt-4 pb-2">
          <BackButton />
        </div>
      </div>

      {/* Hero */}
      <div className="lt-hero pb-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="lt-card p-6 sm:p-10">
            <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
              {/* Avatar */}
              <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-[#E8DFC8] shadow-md">
                {coach.profile_photo_url ? (
                  <img src={coach.profile_photo_url} alt={coach.display_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full lt-avatar flex items-center justify-center rounded-none">
                    <span className="text-4xl font-display">{coach.display_name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap mb-3">
                  <h1 className="text-3xl sm:text-4xl font-display font-medium lt-title tracking-tight" data-testid="coach-name">
                    {coach.display_name}
                  </h1>
                  {coach.subscription_plan && PUBLIC_PLAN_BADGE[coach.subscription_plan] && (() => {
                    const { label, Icon, className } = PUBLIC_PLAN_BADGE[coach.subscription_plan];
                    return (
                      <span
                        data-testid={`public-plan-badge-${coach.subscription_plan}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium ${className}`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {label}
                      </span>
                    );
                  })()}
                </div>
                <div className="flex flex-wrap items-center gap-4 mb-5">
                  {coach.rating_avg > 0 && (
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FEF9E7] border border-[#F5E6A3]">
                      <Star className="w-3.5 h-3.5 lt-star" />
                      <span className="text-[#92710A] text-sm font-semibold">{coach.rating_avg?.toFixed(1)}</span>
                      <span className="lt-muted text-xs">({coach.ratings_count || 0})</span>
                    </div>
                  )}
                  <span className="flex items-center gap-1.5 lt-subtitle text-sm font-body">
                    <Clock className="w-3.5 h-3.5" /> {coach.session_duration || 60} min
                  </span>
                  <span className="flex items-center gap-1.5 lt-subtitle text-sm font-body">
                    <Award className="w-3.5 h-3.5" /> {coach.total_sessions || 0} sessions
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {coach.specialties?.map((s, i) => (
                    <span key={i} className="lt-tag font-body">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-28 sm:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="lt-surface p-6 sm:p-8">
              <h2 className="text-lg font-display font-medium lt-title mb-4">A propos</h2>
              <p className="font-body lt-subtitle leading-relaxed whitespace-pre-line">{coach.bio}</p>
            </motion.div>

            {/* Avis vérifiés — renforce la conversion avant la sidebar de réservation */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.14 }}
              className="lt-surface p-6 sm:p-8"
              data-testid="coach-reviews-wrapper"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-display font-medium lt-title">Avis vérifiés</h2>
                <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-bold">
                  Authentiques
                </span>
              </div>
              <CoachReviewsSection coachUserId={coachId} />
            </motion.div>

            {coach.certifications?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
                className="lt-surface p-6 sm:p-8">
                <h2 className="text-lg font-display font-medium lt-title mb-4">Certifications</h2>
                <ul className="space-y-3">
                  {coach.certifications.map((c, i) => (
                    <li key={i} className="flex items-center gap-3 lt-subtitle text-sm font-body">
                      <div className="w-8 h-8 rounded-lg bg-[#FEF9E7] border border-[#F5E6A3] flex items-center justify-center flex-shrink-0">
                        <Award className="w-4 h-4 text-[#D4AF37]" />
                      </div>
                      {c}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </div>

          {/* Booking sidebar */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <div className="lt-card p-6 sticky top-20">
              {enterpriseCoverage ? (
                <div className="text-center mb-6 pb-5 border-b border-[#E8DFC8]"
                  data-testid="enterprise-coverage-banner">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D1FAE5] text-[#047857] text-[11px] font-semibold uppercase tracking-wider mb-2">
                    <Briefcase className="w-3.5 h-3.5" /> Forfait entreprise
                  </div>
                  <div className="text-[28px] font-display font-semibold text-[#047857] leading-tight">
                    Session incluse
                  </div>
                  <div className="text-[12px] text-[#4B5563] mt-1">
                    {enterpriseCoverage.company_name ? `${enterpriseCoverage.company_name} · ` : ''}
                    <strong className="text-[#111827]" data-testid="enterprise-sessions-remaining">
                      {enterpriseCoverage.sessions_remaining}
                    </strong> session{enterpriseCoverage.sessions_remaining > 1 ? 's' : ''} restante{enterpriseCoverage.sessions_remaining > 1 ? 's' : ''}
                  </div>
                </div>
              ) : (
                <div className="text-center mb-6 pb-5 border-b border-[#E8E5DE]">
                  <span className="text-4xl font-display font-semibold text-[#92710A]">{sessionPrice.toFixed(0)}€</span>
                  <span className="lt-muted text-sm font-body ml-1">/ session ({coach.session_duration || 60} min)</span>
                </div>
              )}

              <div data-testid="booking-theme" className="mb-5">
                <p className="text-xs uppercase tracking-[0.2em] lt-muted mb-3 font-body font-medium">Th&egrave;me de coaching</p>
                {themes.length === 0 ? (
                  <p className="lt-muted text-xs font-body">Chargement...</p>
                ) : (
                  <div className="space-y-3">
                    {/* Professionnel */}
                    {themes.filter(t => t.category === 'professional').length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Briefcase className="w-3.5 h-3.5 text-[#3B82F6]" />
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#3B82F6]">Professionnel</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {themes.filter(t => t.category === 'professional').map(t => (
                            <button key={t.slug} type="button"
                              data-testid={`theme-chip-${t.slug}`}
                              onClick={() => { setSelectedThemeSlug(t.slug); setCustomTheme(''); }}
                              className={`px-3 py-1.5 rounded-full text-xs font-body transition-all ${
                                selectedThemeSlug === t.slug
                                  ? 'bg-[#1a1a1a] text-white font-medium shadow-md'
                                  : 'bg-[#F8F7F4] border border-[#E8E5DE] lt-subtitle hover:border-[#D4AF37]'
                              }`}>
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Personnel */}
                    {themes.filter(t => t.category === 'personal').length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Heart className="w-3.5 h-3.5 text-[#EC4899]" />
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#EC4899]">Personnel</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {themes.filter(t => t.category === 'personal').map(t => (
                            <button key={t.slug} type="button"
                              data-testid={`theme-chip-${t.slug}`}
                              onClick={() => { setSelectedThemeSlug(t.slug); setCustomTheme(''); }}
                              className={`px-3 py-1.5 rounded-full text-xs font-body transition-all ${
                                selectedThemeSlug === t.slug
                                  ? 'bg-[#1a1a1a] text-white font-medium shadow-md'
                                  : 'bg-[#F8F7F4] border border-[#E8E5DE] lt-subtitle hover:border-[#D4AF37]'
                              }`}>
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Personnalisé */}
                    <div>
                      <button type="button"
                        data-testid="theme-chip-custom"
                        onClick={() => setSelectedThemeSlug('custom')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-body transition-all ${
                          selectedThemeSlug === 'custom'
                            ? 'bg-[#1a1a1a] text-white font-medium shadow-md'
                            : 'bg-[#F8F7F4] border border-[#E8E5DE] lt-subtitle hover:border-[#D4AF37]'
                        }`}>
                        <Pencil className="w-3 h-3" /> Autre (personnalis&eacute;)
                      </button>
                      {selectedThemeSlug === 'custom' && (
                        <input
                          type="text"
                          data-testid="theme-custom-input"
                          value={customTheme}
                          onChange={(e) => setCustomTheme(e.target.value.slice(0, 120))}
                          placeholder="Pr&eacute;cisez votre th&egrave;me..."
                          className="mt-2 w-full px-3 py-2 rounded-xl bg-white border border-[#E8E5DE] text-sm font-body text-[#1C1147] focus:outline-none focus:border-[#D4AF37]"
                          autoFocus
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div data-testid="booking-calendar" className="mb-5">
                <p className="text-xs uppercase tracking-[0.2em] lt-muted mb-3 font-body font-medium">Choisir une date</p>
                <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect}
                  disabled={(date) => {
                    if (date < new Date()) return true;
                    const dateStr = toLocalDateStr(date);
                    // Grey out dates with no available slots
                    if (Object.keys(monthAvailability).length > 0) {
                      const slots = monthAvailability[dateStr];
                      return !slots || slots.length === 0;
                    }
                    return false;
                  }}
                  className="rounded-xl border border-[#E8E5DE] bg-white" />
              </div>

              {selectedDate && (
                <div data-testid="time-slots" className="mb-5">
                  <p className="text-xs uppercase tracking-[0.2em] lt-muted mb-3 font-body font-medium">
                    Créneaux — {selectedDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                  </p>
                  {daySlots.length === 0 ? (
                    <p className="lt-muted text-sm text-center py-4 font-body">Aucun créneau disponible</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {daySlots.map((slot, i) => (
                        <button key={i} data-testid={`time-slot-${slot.start_time}`}
                          onClick={() => setSelectedSlot(slot)}
                          className={`py-2.5 px-3 rounded-xl text-sm font-body transition-all ${
                            selectedSlot?.start_time === slot.start_time
                              ? 'bg-[#1a1a1a] text-white font-medium shadow-md'
                              : 'bg-[#F8F7F4] border border-[#E8E5DE] lt-subtitle hover:border-[#D4AF37]'
                          }`}>
                          {formatTimeFR(slot.start_time)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button data-testid="book-session-button" onClick={handleBook}
                disabled={!selectedSlot || booking || !selectedThemeSlug || (selectedThemeSlug === 'custom' && customTheme.trim().length < 3)}
                className="w-full py-3.5 lt-btn-primary font-display text-sm disabled:opacity-30 flex items-center justify-center gap-2">
                {booking ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirection...</> : <>R&eacute;server cette session <ChevronRight className="w-4 h-4" /></>}
              </button>
              {!user && <p className="text-center lt-muted text-xs mt-3 font-body">Connectez-vous pour réserver.</p>}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
